/**
 * Profile photo upload module — presigned S3 POST flow.
 *
 * On file selection:
 *   1. Validate size (≤6 MB), MIME type, real image, and dimensions via createImageBitmap.
 *   2. Fetch a presigned POST (policy + form fields) from Django (policy enforces 1–6 MB).
 *   3. POST the file to S3 via FormData (bypasses CloudFront/Django).
 *   4. On success: set the hidden profile_photo_key input, clear the file input,
 *      show a success badge.
 *   5. On any failure: show an error alert, reset state.
 *
 * On form submit:
 *   Guard against submitting while an upload is still in progress.
 */

const MAX_SIZE_BYTES = 6 * 1024 * 1024; // 6 MB (must match presigned POST policy)
const MAX_DIMENSION = 6000;

async function validateImage(file) {
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image (JPEG, PNG, or WebP).');
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error('Image must not exceed 6 MB.');
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('Invalid or corrupted image file.');
  }

  const { width, height } = bitmap;
  bitmap.close();

  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    throw new Error(`Image dimensions must not exceed ${MAX_DIMENSION}×${MAX_DIMENSION} pixels.`);
  }
}

async function fetchPresignedUrl(ext, endpointUrl) {
  const url = `${endpointUrl}?ext=${encodeURIComponent(ext)}`;
  const response = await fetch(url, { credentials: 'same-origin' });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Could not get upload URL. Please try again.');
  }
  return response.json();
}

/**
 * Upload file to S3 using presigned POST (policy + form fields).
 * FormData must include all fields from the policy, then the file as "file".
 */
async function uploadToS3(postUrl, fields, file) {
  const formData = new FormData();
  for (const [name, value] of Object.entries(fields)) {
    formData.append(name, value);
  }
  formData.append('file', file);

  const response = await fetch(postUrl, {
    method: 'POST',
    body: formData,
  });
  if (!response.ok) {
    throw new Error('Upload to S3 failed. Please try again.');
  }
}

function getFileExtension(file) {
  const parts = file.name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

function getCsrfToken() {
  return document.querySelector('[name=csrfmiddlewaretoken]')?.value ?? '';
}

async function deletePendingUpload(key, endpointUrl) {
  if (!key) return;
  try {
    await fetch(endpointUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRFToken': getCsrfToken(),
      },
      body: `key=${encodeURIComponent(key)}`,
    });
  } catch {
    // best-effort; S3 lifecycle rule is the authoritative safety net
  }
}

function showError(errorEl, message) {
  errorEl.textContent = message;
  errorEl.classList.remove('d-none');
}

function hideError(errorEl) {
  errorEl.textContent = '';
  errorEl.classList.add('d-none');
}

function updatePreview(file, previewImageId) {
  if (!previewImageId) return;
  // Query fresh at call time — guaranteed live DOM reference.
  const previewEl = document.getElementById(previewImageId);
  if (!previewEl) return;

  const objectUrl = URL.createObjectURL(file);
  previewEl.src = objectUrl;
  previewEl.addEventListener('load', () => URL.revokeObjectURL(objectUrl), { once: true });
}

function setStatus(statusEl, state) {
  statusEl.classList.remove('d-none');
  const spinner     = statusEl.querySelector('.profile-photo-spinner');
  const successText = statusEl.querySelector('.profile-photo-success');

  if (state === 'uploading') {
    if (spinner)     spinner.classList.remove('d-none');
    if (successText) successText.classList.add('d-none');
  } else if (state === 'done') {
    if (spinner)     spinner.classList.add('d-none');
    if (successText) successText.classList.remove('d-none');
  } else {
    statusEl.classList.add('d-none');
  }
}

const PROFILE_UPLOAD_CONFIG = {
  fileInputId: 'id_profile_photo',
  keyInputId: 'id_profile_photo_key',
  errorElementId: 'profile-photo-upload-error',
  statusElementId: 'profile-photo-upload-status',
  previewImageId: 'profile-photo-preview',
  presignedUrlEndpoint: '/account/profile/presigned-upload-url/',
  deleteTempEndpoint: '/account/profile/delete-temp-photo/',
};

export function initImageUpload(config = {}) {
  const resolved = { ...PROFILE_UPLOAD_CONFIG, ...config };
  const fileInput = document.getElementById(resolved.fileInputId);
  if (!fileInput) return;

  const keyInput  = document.getElementById(resolved.keyInputId);
  const errorEl   = document.getElementById(resolved.errorElementId);
  const statusEl  = document.getElementById(resolved.statusElementId);
  const form      = fileInput.closest('form');
  const submitBtn = form ? form.querySelector('[type="submit"]') : null;

  if (!keyInput || !errorEl || !statusEl) {
    console.error('[profilePhotoUpload] required DOM elements not found', { keyInput, errorEl, statusEl });
    return;
  }

  let uploading = false;

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files[0];
    if (!file) return;

    // Show spinner immediately — before any async work so it's always visible.
    hideError(errorEl);
    setStatus(statusEl, 'uploading');
    uploading = true;
    if (submitBtn) submitBtn.disabled = true;

    // If a previous upload exists in this session, delete it before starting a new one.
    if (keyInput.value) {
      await deletePendingUpload(keyInput.value, resolved.deleteTempEndpoint);
      keyInput.value = '';
    }

    try {
      await validateImage(file);

      // Show preview immediately after local validation — independent of S3.
      updatePreview(file, resolved.previewImageId);

      const ext = getFileExtension(file);
      const { url, fields, key } = await fetchPresignedUrl(ext, resolved.presignedUrlEndpoint);

      await uploadToS3(url, fields, file);

      keyInput.value = key;
      fileInput.value = '';
      setStatus(statusEl, 'done');
    } catch (err) {
      keyInput.value = '';
      setStatus(statusEl, 'hidden');
      showError(errorEl, err.message);
    } finally {
      uploading = false;
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  // Guard: prevent submit while upload is in progress
  if (form) {
    form.addEventListener('submit', (e) => {
      if (uploading) {
        e.preventDefault();
        showError(errorEl, 'Please wait for the photo upload to finish before saving.');
      }
    });
  }
}

export function initProfilePhotoUpload() {
  initImageUpload(PROFILE_UPLOAD_CONFIG);
}
